import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FC, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "sonner";
import { useWalletObserver, useWalletLoadingState } from "@sundaeswap/wallet-lite";

import { ProvidersWrapper } from "./components/ProvidersWrapper";
import {
  DevEnvironmentGate,
  DevEnvironmentStrip,
  useDevEnvironmentNotice,
} from "./components/DevEnvironmentNotice";
import { RevealOverlay } from "./components/RevealOverlay";
import { GummiwormBalance } from "./components/GummiwormBalance";
import { Header } from "./components/Header";
import { WalletBalance } from "./components/WalletBalance";
import { DepositOverlay } from "./components/DepositOverlay";
import type { DepositEntry } from "./components/DepositOverlay/AmountStep";
import { WithdrawOverlay } from "./components/WithdrawOverlay";
import { IntroStep } from "./components/DepositOverlay/IntroStep";
import { TradingView } from "./components/TradingView";
import { NewsPage } from "./components/NewsPage";
import { Leaderboard } from "./components/Leaderboard";
import { AboutView } from "./components/AboutView";
import { RulesView } from "./components/RulesView";
import { useGummiwormContext } from "./context/GummiwormContext";
import { useNetworkCheck } from "./hooks/useNetworkCheck";
import { useOrderEncryptionHandshake } from "./hooks/useOrderEncryptionHandshake";
import { pathForView, View, viewFromPath } from "./lib/viewRouting";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    },
  },
});

const ErrorFallback = ({ error }: { error: Error }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="p-6 rounded-xl bg-sr-surface max-w-sm mx-4">
      <h2 className="text-lg font-medium text-white mb-3">
        Something went wrong
      </h2>
      <pre className="text-xs text-sr-salmon bg-sr-salmon/10 p-3 rounded-lg overflow-auto">
        {error.message}
      </pre>
    </div>
  </div>
);

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-6 h-6 rounded-full border-2 border-sr-lemon-muted border-t-transparent animate-spin" />
  </div>
);

const MainContent: FC = () => {
  const { activeWallet } = useWalletObserver();
  const { ready: walletReady } = useWalletLoadingState();

  const isConnected = !!activeWallet;
  const { mismatch: networkMismatch } = useNetworkCheck();
  const { ledgerView, myAccountId, accountsReady, tradingEnabled, maintenanceMode, maintenanceUntilMs, error } = useGummiwormContext();
  const myAccount = ledgerView.account;

  useOrderEncryptionHandshake();

  const {
    notice: devEnvNotice,
    acknowledged: devEnvAcknowledged,
    acknowledge: acknowledgeDevEnv,
  } = useDevEnvironmentNotice();

  // Determine initial view: wait for wallet + accounts to settle before deciding.
  // A path that names a view is an answer, so there is nothing to wait for:
  // commit it immediately and mark the auto-switch spent, or the effects below
  // would send anyone with an account straight back to the exchange.
  const routedView = useRef(viewFromPath(window.location.pathname)).current;
  const [view, setView] = useState<View | null>(routedView);
  const hasAutoSwitched = useRef(routedView !== null);
  const [tradeMarketKey, setTradeMarketKey] = useState<string | null>(null);
  const goToMarket = useCallback((marketKey: string) => {
    setTradeMarketKey(marketKey);
    setView("trade");
  }, []);
  const minDelayPassed = useRef(false);
  const [initWarning, setInitWarning] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      minDelayPassed.current = true;
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Wait for wallet detection to finish before committing a view, even when
    // not yet connected: a wallet that auto-connects a moment later would flip
    // deposit->trade otherwise. walletReady resolves quickly (true even with no
    // wallet installed), and the 8s fallback below bounds any hang.
    if (!walletReady) return;
    if (isConnected && !accountsReady) return;

    const timer = setTimeout(() => {
      // The exchange is viewable before the bell: an empty-handed visitor
      // should see the book and the activity, not a locked door. Only order
      // submission is gated (the ticket's cta), so landing here is safe.
      if (myAccount && !hasAutoSwitched.current) {
        hasAutoSwitched.current = true;
        setView("trade");
      } else if (view === null) {
        setView(isConnected && myAccount ? "trade" : "deposit");
      }
    }, minDelayPassed.current ? 0 : 300);
    return () => clearTimeout(timer);
  }, [walletReady, isConnected, accountsReady, myAccount, tradingEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the address bar on the current tab. One effect on `view`, so header
  // clicks and programmatic switches (the reveal, "trade this market" from the
  // news feed) are all covered without touching each call site.
  //
  // push vs replace decides what the back button does. Arriving at "/" and
  // being resolved to the exchange is not a navigation the user made, so it
  // REPLACES -- otherwise back would return to "/", which would resolve to the
  // exchange again and trap them. Moving between tabs from a path that already
  // names one is a real navigation, so it pushes.
  useEffect(() => {
    if (view === null) return;
    const target = pathForView(view);
    if (window.location.pathname === target) return;
    const cameFromAView = viewFromPath(window.location.pathname) !== null;
    // Carry search and hash through: the wallet and auth round-trips can land
    // back here with query parameters that other code still needs to read.
    const url = `${target}${window.location.search}${window.location.hash}`;
    if (cameFromAView) {
      window.history.pushState({}, "", url);
    } else {
      window.history.replaceState({}, "", url);
    }
  }, [view]);

  // Back and forward. The effect above sees the path already matches and does
  // not push, so this cannot fight it.
  useEffect(() => {
    const onPopState = () => {
      const next = viewFromPath(window.location.pathname);
      if (next) setView(next);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    // A failed connection is worth saying even when a remembered tab means
    // there is no view to fall back to -- the data on screen is still partial.
    if (error) {
      setInitWarning(
        view === null
          ? "WebSocket did not connect. Showing the deposits view with partial data while the connection recovers."
          : "WebSocket did not connect. Showing partial data while the connection recovers.",
      );
      if (view === null) setView("deposit");
      return;
    }

    if (view !== null) return;

    const timer = setTimeout(() => {
      setInitWarning(
        "Account data is taking longer than expected to load. Showing the deposits view while initialization catches up.",
      );
      setView((current) => current ?? "deposit");
    }, 8000);

    return () => clearTimeout(timer);
  }, [view, error]);

  useEffect(() => {
    if (error) return;
    if (!walletReady && isConnected) return;
    if (isConnected && !accountsReady) return;
    setInitWarning(null);
  }, [error, walletReady, isConnected, accountsReady]);

  // Reveal animation: when trading flips on and user has an account
  const [revealing, setRevealing] = useState(false);
  const [revealWidgets, setRevealWidgets] = useState(false);
  const wasTradingDisabled = useRef(!tradingEnabled);
  // Show the branded reveal at most once per browser session, so no reconnect
  // or transient trading toggle can re-flash it.
  const hasRevealedThisSession = useRef(
    typeof sessionStorage !== "undefined" && sessionStorage.getItem("sr:seenReveal") === "1",
  );

  useEffect(() => {
    // Only trigger reveal if we were on the deposit screen and trading just got enabled
    if (tradingEnabled && wasTradingDisabled.current && myAccount && view === "deposit" && !hasRevealedThisSession.current) {
      wasTradingDisabled.current = false;
      hasRevealedThisSession.current = true;
      try { sessionStorage.setItem("sr:seenReveal", "1"); } catch { /* sessionStorage unavailable */ }
      setRevealing(true);
    } else if (tradingEnabled && myAccount && !hasAutoSwitched.current) {
      // Direct switch (page loaded with trading already enabled)
      hasAutoSwitched.current = true;
      setView("trade");
    }
    if (!tradingEnabled) {
      wasTradingDisabled.current = true;
    }
  }, [tradingEnabled, myAccount, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRevealSwitch = () => {
    // Called while overlay is opaque — switch to exchange behind it, widgets hidden
    hasAutoSwitched.current = true;
    setRevealWidgets(true);
    setView("trade");
  };

  const handleRevealComplete = () => {
    // Overlay gone — widgets are already animating in from the render
    setRevealing(false);
    setTimeout(() => setRevealWidgets(false), 2500);
  };
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositShellScaled, setDepositShellScaled] = useState(false);
  const [viewRequestId, setViewRequestId] = useState<string | null>(null);
  const [depositAssetId, setDepositAssetId] = useState<string | null>(null);
  const [resumeEntries, setResumeEntries] = useState<DepositEntry[] | null>(
    null,
  );

  // Returning from the X OAuth redirect: the page reloaded, so restore the
  // stashed deposit and reopen the overlay at the review step.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("xauth")) return;
    window.history.replaceState(null, "", window.location.pathname);
    let saved: { entries?: unknown } | null = null;
    try {
      const raw = sessionStorage.getItem("sr:resumeDeposit");
      sessionStorage.removeItem("sr:resumeDeposit");
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }
    if (params.get("xauth") === "ok" && Array.isArray(saved?.entries)) {
      setResumeEntries(saved.entries as DepositEntry[]);
      setDepositShellScaled(true);
      setDepositOpen(true);
    }
  }, []);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [viewWithdrawId, setViewWithdrawId] = useState<string | null>(null);

  const openWithdrawFlow = (options?: { withdrawId?: string | null }) => {
    if (networkMismatch) return;
    setDepositShellScaled(true);
    setViewWithdrawId(options?.withdrawId ?? null);
    setWithdrawOpen(true);
  };

  const openDepositFlow = (options?: { requestId?: string | null; assetId?: string | null }) => {
    if (networkMismatch) return;
    setDepositShellScaled(true);
    setViewRequestId(options?.requestId ?? null);
    setDepositAssetId(options?.assetId ?? null);
    setDepositOpen(true);
  };

  const handleDepositCloseStart = () => {
    setDepositShellScaled(false);
  };

  return (
    // Desktop trade view pins the app to exactly one viewport (lg:h-screen +
    // overflow-hidden down the chain) so the chart/grid can't stretch past the
    // screen to match a tall order form; panels scroll internally instead.
    // Every other view keeps the min-h-screen grow-with-content behavior.
    <div className={`min-h-screen flex flex-col ${view === "trade" ? "lg:h-screen" : ""}`}>
      {/* Stands outside the app shell so the blur/scale the overlays apply never softens it. */}
      {devEnvNotice && !devEnvAcknowledged && (
        <DevEnvironmentGate
          competitionUrl={devEnvNotice.competitionUrl}
          onContinue={acknowledgeDevEnv}
        />
      )}
      <RevealOverlay active={revealing} onSwitch={handleRevealSwitch} onComplete={handleRevealComplete} />
      <div
        className={`sr-app-shell ${view === "trade" ? "lg:min-h-0 lg:h-full lg:overflow-hidden" : ""} ${depositShellScaled ? "sr-app-shell-overlay-open" : ""}`}
      >
        {devEnvNotice && <DevEnvironmentStrip competitionUrl={devEnvNotice.competitionUrl} />}

        <Header view={view ?? "deposit"} onViewChange={setView} networkMismatch={networkMismatch} tradingEnabled={tradingEnabled} hideLogo={revealing} />

        {/* Maintenance is refused server-side, so say it once at the top rather
            than letting people discover it by filling in a trade. */}
        {maintenanceMode && (
          <div className="px-2 pt-2 lg:px-4">
            <div className="rounded-xl border border-sr-tangerine/30 bg-sr-tangerine/10 px-4 py-3">
              <p className="text-sm font-medium text-sr-tangerine">
                Under construction
              </p>
              <p className="mt-0.5 text-xs text-sr-neutral-400">
                Sugar Rush is down for maintenance. Trading and deposits are
                paused
                {maintenanceUntilMs !== null && maintenanceUntilMs > Date.now()
                  ? `, expected back around ${new Date(maintenanceUntilMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`
                  : "; we'll be back shortly."}
              </p>
            </div>
          </div>
        )}

        {initWarning && (
          <div className="px-2 pt-2 lg:px-4">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-sr-tangerine/30 bg-sr-tangerine/10 px-4 py-3">
              <p className="text-sm text-sr-oat-milk">
                {initWarning}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="shrink-0 rounded-lg border border-sr-border-subtle px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/5"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {view === null ? (
          <main className="flex-1 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-sr-lemon-muted border-t-transparent animate-spin" />
          </main>
        ) : view === "trade" ? (
          <main className="flex-1 flex flex-col bg-sr-espresso px-2 pb-4 lg:min-h-0 lg:overflow-hidden lg:px-4">
            <TradingView
              onDeposit={(assetId) => openDepositFlow({ assetId })}
              depositDisabled={!!networkMismatch}
              revealWidgets={revealWidgets}
              requestedMarketKey={tradeMarketKey}
            />
          </main>
        ) : view === "news" ? (
          <main className="flex-1 flex flex-col items-center bg-sr-espresso px-4 pb-6 pt-2 sm:pb-12">
            <NewsPage onTradeMarket={goToMarket} />
          </main>
        ) : view === "leaderboard" ? (
          <main className="flex-1 flex flex-col items-center bg-sr-espresso px-4 pb-6 pt-2 sm:pb-12">
            <Leaderboard />
          </main>
        ) : view === "about" ? (
          <main className="flex-1 flex flex-col items-center px-4 pb-6 sm:pb-12">
            <AboutView onShowRules={() => setView("rules")} />
          </main>
        ) : view === "rules" ? (
          <main className="flex-1 flex flex-col items-center px-4 pb-6 sm:pb-12">
            <RulesView />
          </main>
        ) : (
          <main className="flex-1 flex flex-col items-center px-4 pb-6 sm:pb-12">
            {!isConnected ? (
              <div className="flex w-full max-w-[420px] flex-1 flex-col items-center justify-center gap-8 py-10">
                <IntroStep
                  isConnected={false}
                  onBegin={() => openDepositFlow()}
                  title={`Welcome to ${tradingEnabled ? "Sugar Rush" : "Gummiworm"}`}
                />
              </div>
            ) : (
              <div className="w-full max-w-md flex flex-col gap-6">
                <WalletBalance onDeposit={() => openDepositFlow()} onWithdraw={() => openWithdrawFlow()} depositDisabled={!!networkMismatch} />

                <GummiwormBalance
                  onViewDeposit={(id) => {
                    openDepositFlow({ requestId: id });
                  }}
                  onViewWithdraw={(id) => {
                    openWithdrawFlow({ withdrawId: id });
                  }}
                />

                {myAccount && !tradingEnabled && (
                  <div className="sr-card p-5 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="w-4 h-4 rounded-full border-2 border-sr-lemon-muted border-t-transparent animate-spin" />
                      <span className="text-sm font-medium text-sr-lemon-muted">
                        Any minute now...
                      </span>
                    </div>
                    <p className="text-xs text-sr-neutral-500">
                      Your funds are on the L2. Stay tuned — something's coming.
                    </p>
                  </div>
                )}
              </div>
            )}
          </main>
        )}
      </div>

      <DepositOverlay
        open={depositOpen}
        onCloseStart={handleDepositCloseStart}
        onClose={() => {
          setDepositOpen(false);
          setViewRequestId(null);
          setDepositAssetId(null);
          setResumeEntries(null);
        }}
        viewRequestId={viewRequestId}
        initialAssetId={depositAssetId}
        resumeEntries={resumeEntries}
      />

      <WithdrawOverlay
        open={withdrawOpen}
        onCloseStart={() => setDepositShellScaled(false)}
        onClose={() => {
          setWithdrawOpen(false);
          setViewWithdrawId(null);
          setDepositShellScaled(false);
        }}
        viewWithdrawId={viewWithdrawId}
      />

      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "var(--color-sr-surface)",
            color: "var(--color-sr-oat-milk)",
            border: "1px solid var(--color-sr-border-subtle)",
          },
        }}
      />
    </div>
  );
};

export const App: FC = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ProvidersWrapper>
            <MainContent />
          </ProvidersWrapper>
        </QueryClientProvider>
      </Suspense>
    </ErrorBoundary>
  );
};
